import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogIn, Menu, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, setToken } from "../lib/api";

export const Shell = ({ user, setUser, children }) => {
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const navigate = useNavigate();
  const close = () => setOpen(false);
  const logout = async () => { await api.post("/auth/logout").catch(() => {}); setToken(null); setUser(null); close(); navigate("/"); };
  return (
    <div className="app-shell">
      <header className="nav">
        <Link className="brand" data-testid="brand-home-link" to="/" onClick={close}><span className="brand-mark">A</span><span>AVAILITY<small>Certification platform</small></span></Link>
        <button className="menu-button" data-testid="mobile-menu-button" onClick={() => setOpen(!open)} aria-label="Toggle menu">{open ? <X /> : <Menu />}</button>
        <nav className={open ? "nav-links open" : "nav-links"} onClick={close}>
          <Link data-testid="nav-certifications-link" to="/certifications">Certifications</Link>
          <Link data-testid="nav-verify-link" to="/verify">Verify certificate</Link>
          {user ? <>
            <Link data-testid="nav-dashboard-link" to="/dashboard">My learning</Link>
            {user.role === "admin" && <Link data-testid="nav-admin-link" to="/admin">Admin console</Link>}
            {user.has_password && <button className="text-button" data-testid="nav-change-password-button" onClick={() => setPwOpen(true)}>Password</button>}
            <button className="text-button" data-testid="nav-logout-button" onClick={logout}>Sign out</button>
          </> : <Link className="nav-login" data-testid="nav-login-link" to="/login"><LogIn size={16} /> Sign in</Link>}
        </nav>
      </header>
      {children}
      {pwOpen && <ChangePassword onClose={() => setPwOpen(false)} />}
      <footer>
        <div><span className="brand footer-brand"><span className="brand-mark">A</span> AVAILITY</span><p>Independent, verifiable certification for professionals in healthcare, business, technology and people roles.</p></div>
        <div className="footer-links"><Link data-testid="footer-about-link" to="/about">About</Link><Link data-testid="footer-faq-link" to="/faq">FAQ</Link><Link data-testid="footer-contact-link" to="/contact">Contact</Link><Link data-testid="footer-refund-link" to="/refund-policy">Refunds</Link><Link data-testid="footer-privacy-link" to="/privacy">Privacy</Link><Link data-testid="footer-terms-link" to="/terms">Terms</Link><Link data-testid="footer-verify-link" to="/verify">Verify a certificate</Link></div>
        <p className="footer-note" data-testid="footer-contact-note">© 2026 Availity.si · Contact us: <a href="mailto:help@availity.si">help@availity.si</a> · <Link to="/contact">Contact form</Link></p>
      </footer>
    </div>
  );
};

export const Modal = ({ open, onClose, title, children, testId }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className="modal" role="dialog" aria-modal="true" data-testid={testId} onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head"><h2>{title}</h2><button className="icon-button" data-testid={`${testId}-close`} onClick={onClose} aria-label="Close"><X size={18} /></button></div>
        {children}
      </div>
    </div>
  );
};

export const Loading = ({ label }) => <div className="loading" data-testid="page-loading">{label}</div>;

const ChangePassword = ({ onClose }) => {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (form.new_password !== form.confirm) return setError("New passwords do not match");
    try {
      const r = await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      setToken(r.data.token); toast.success("Password updated"); onClose();
    } catch (err) { setError(formatError(err)); }
  };
  const field = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <Modal open onClose={onClose} title="Change password" testId="change-password-dialog">
      <form className="admin-form" onSubmit={submit}>
        <label className="form-field"><span>Current password</span><input type="password" data-testid="current-password-input" value={form.current_password} onChange={field("current_password")} required /></label>
        <label className="form-field"><span>New password (10+ characters)</span><input type="password" minLength={10} data-testid="new-password-input" value={form.new_password} onChange={field("new_password")} required /></label>
        <label className="form-field"><span>Confirm new password</span><input type="password" minLength={10} data-testid="confirm-password-input" value={form.confirm} onChange={field("confirm")} required /></label>
        {error && <div className="error" data-testid="change-password-error">{error}</div>}
        <button className="button primary full" data-testid="change-password-submit">Update password</button>
      </form>
    </Modal>
  );
};
